import { FormGroup, Text } from '@ksyos/design-system';
import { NumberInput } from '@frontend/components/fields/Number';
import type { ValidationErrors } from '../../../../utils/validation';
import { StyledBorderAndPadding } from '../../../elements/common';
import { NullableTextField } from '../../../fields/NullableText';
import { OrthOExaminationOrder } from './orthOExaminationOrder';

interface IOrthOV1ProcedureCheckedVisusExaminationOdOsRowProps {
    additie_od?: string | null;
    additie_os?: string | null;
    as_od?: string | null;
    as_os?: string | null;
    cylindrisch_od?: string | null;
    cylindrisch_os?: string | null;
    errors?: ValidationErrors;
    field: string;
    hasErrors: boolean;
    isReadOnly?: boolean;
    isRequired?: boolean;
    label: string;
    onDoValidate?: ((name: string, index?: number) => void) | ((name: string) => void) | undefined;
    prisma_degrees_od: number | null;
    prisma_degrees_os: number | null;
    prisma_od?: string | null;
    prisma_os?: string | null;
    sferisch_od?: string | null;
    sferisch_os?: string | null;
}

interface IReadOrEditInputFieldProps {
    automation: string;
    className?: string;
    descriptionText?: string;
    descriptionTextClassName?: string;
    error?: string;
    fieldKey: string;
    fieldValue?: string | number | null;
    isReadOnly?: boolean;
    label: string;
    onDoValidate?: () => void;
    placeholder: string;
    postfix?: string;
    prefix: string;
    tabIndex: OrthOExaminationOrder;
}

export function OrthOV1ProcedureCheckedVisusExaminationOdOsRow(
    props: IOrthOV1ProcedureCheckedVisusExaminationOdOsRowProps
) {
    return (
        <>
            {showRow() && (
                <FormGroup
                    hasErrors={props.hasErrors}
                    isRequired={!props.isReadOnly && props.isRequired}
                    label={props.label}
                >
                    <div className="row">
                        <div
                            className="col-sm-8"
                            style={{ minWidth: 240 }}
                        >
                            <StyledBorderAndPadding>
                                <Text
                                    fontWeight="bold"
                                    size="xs"
                                >
                                    OD
                                </Text>
                                <div className="mb-3 row">
                                    <div className="col-8">
                                        <ReadOrEditInputField
                                            automation={`${props.field}_sferisch_od`}
                                            error={props.errors?.get(`customControlData.${props.field}_sferisch_od`)}
                                            fieldKey={`${props.field}_sferisch_od`}
                                            fieldValue={props.sferisch_od}
                                            isReadOnly={props.isReadOnly}
                                            label={''}
                                            placeholder={'sph'}
                                            prefix="S"
                                            tabIndex={OrthOExaminationOrder.checkedVisionExaminationOd}
                                        />
                                    </div>
                                    <div className="col-8">
                                        <ReadOrEditInputField
                                            automation={`${props.field}_cylindrisch_od`}
                                            className={'has-prefix-text'}
                                            descriptionText={'-'}
                                            descriptionTextClassName={'font-weight-bold'}
                                            error={props.errors?.get(`customControlData.${props.field}_cylindrisch_od`)}
                                            fieldKey={`${props.field}_cylindrisch_od`}
                                            fieldValue={props.cylindrisch_od}
                                            isReadOnly={props.isReadOnly}
                                            label={''}
                                            onDoValidate={() => {
                                                if (props.onDoValidate) {
                                                    props.onDoValidate(`${props.field}_cylindrisch_od`);
                                                    props.onDoValidate(`${props.field}_as_od`);
                                                }
                                            }}
                                            placeholder={'cyl'}
                                            prefix="C"
                                            tabIndex={OrthOExaminationOrder.checkedVisionExaminationOd}
                                        />
                                    </div>
                                    <div className="col-8">
                                        <ReadOrEditInputField
                                            automation={`${props.field}_as_od`}
                                            error={props.errors?.get(`customControlData.${props.field}_as_od`)}
                                            fieldKey={`${props.field}_as_od`}
                                            fieldValue={props.as_od}
                                            isReadOnly={props.isReadOnly}
                                            label={''}
                                            onDoValidate={() => {
                                                if (props.onDoValidate) {
                                                    props.onDoValidate(`${props.field}_cylindrisch_od`);
                                                    props.onDoValidate(`${props.field}_as_od`);
                                                }
                                            }}
                                            placeholder={'ax'}
                                            prefix="as"
                                            tabIndex={OrthOExaminationOrder.checkedVisionExaminationOd}
                                        />
                                    </div>
                                </div>
                                <div className="mb-3 row">
                                    <div className="col-8">
                                        <ReadOrEditInputField
                                            automation={`${props.field}_additie_od`}
                                            error={props.errors?.get(`customControlData.${props.field}_additie_od`)}
                                            fieldKey={`${props.field}_additie_od`}
                                            fieldValue={props.additie_od}
                                            isReadOnly={props.isReadOnly}
                                            label={''}
                                            placeholder={'add'}
                                            prefix="add"
                                            tabIndex={OrthOExaminationOrder.checkedVisionExaminationOd}
                                        />
                                    </div>
                                    <div className="col-8">
                                        <ReadOrEditInputField
                                            automation={`${props.field}_prisma_od`}
                                            error={props.errors?.get(`customControlData.${props.field}_prisma_od`)}
                                            fieldKey={`${props.field}_prisma_od`}
                                            fieldValue={props.prisma_od}
                                            isReadOnly={props.isReadOnly}
                                            label={''}
                                            onDoValidate={() => {
                                                if (props.onDoValidate) {
                                                    props.onDoValidate(`${props.field}_prisma_degrees_od`);
                                                    props.onDoValidate(`${props.field}_prisma_od`);
                                                }
                                            }}
                                            placeholder={'pr'}
                                            prefix="pr"
                                            tabIndex={OrthOExaminationOrder.checkedVisionExaminationOd}
                                        />
                                    </div>
                                    <div className="col-8">
                                        <ReadOrEditNumberInputField
                                            automation={`${props.field}_prisma_degrees_od`}
                                            error={props.errors?.get(
                                                `customControlData.${props.field}_prisma_degrees_od`
                                            )}
                                            fieldKey={`${props.field}_prisma_degrees_od`}
                                            fieldValue={props.prisma_degrees_od}
                                            isReadOnly={props.isReadOnly}
                                            label={''}
                                            onDoValidate={() => {
                                                if (props.onDoValidate) {
                                                    props.onDoValidate(`${props.field}_prisma_degrees_od`);
                                                    props.onDoValidate(`${props.field}_prisma_od`);
                                                }
                                            }}
                                            placeholder={'     °'}
                                            postfix="°"
                                            prefix=""
                                            tabIndex={OrthOExaminationOrder.checkedVisionExaminationOd}
                                        />
                                    </div>
                                </div>
                            </StyledBorderAndPadding>
                        </div>
                        <div
                            className="col-sm-8"
                            style={{ minWidth: 240 }}
                        >
                            <StyledBorderAndPadding>
                                <Text
                                    fontWeight="bold"
                                    size="xs"
                                >
                                    OS
                                </Text>
                                <div className="mb-3 row">
                                    <div className="col-8">
                                        <ReadOrEditInputField
                                            automation={`${props.field}_sferisch_os`}
                                            error={props.errors?.get(`customControlData.${props.field}_sferisch_os`)}
                                            fieldKey={`${props.field}_sferisch_os`}
                                            fieldValue={props.sferisch_os}
                                            isReadOnly={props.isReadOnly}
                                            label={''}
                                            placeholder={'sph'}
                                            prefix="S"
                                            tabIndex={OrthOExaminationOrder.checkedVisionExaminationOs}
                                        />
                                    </div>
                                    <div className="col-8">
                                        <ReadOrEditInputField
                                            automation={`${props.field}_cylindrisch_os`}
                                            className={'has-prefix-text'}
                                            descriptionText={'-'}
                                            descriptionTextClassName={'font-weight-bold'}
                                            error={props.errors?.get(`customControlData.${props.field}_cylindrisch_os`)}
                                            fieldKey={`${props.field}_cylindrisch_os`}
                                            fieldValue={props.cylindrisch_os}
                                            isReadOnly={props.isReadOnly}
                                            label={''}
                                            onDoValidate={() => {
                                                if (props.onDoValidate) {
                                                    props.onDoValidate(`${props.field}_cylindrisch_os`);
                                                    props.onDoValidate(`${props.field}_as_os`);
                                                }
                                            }}
                                            placeholder={'cyl'}
                                            prefix="C"
                                            tabIndex={OrthOExaminationOrder.checkedVisionExaminationOs}
                                        />
                                    </div>
                                    <div className="col-8">
                                        <ReadOrEditInputField
                                            automation={`${props.field}_as_os`}
                                            error={props.errors?.get(`customControlData.${props.field}_as_os`)}
                                            fieldKey={`${props.field}_as_os`}
                                            fieldValue={props.as_os}
                                            isReadOnly={props.isReadOnly}
                                            label={''}
                                            onDoValidate={() => {
                                                if (props.onDoValidate) {
                                                    props.onDoValidate(`${props.field}_cylindrisch_os`);
                                                    props.onDoValidate(`${props.field}_as_os`);
                                                }
                                            }}
                                            placeholder={'ax'}
                                            prefix="as"
                                            tabIndex={OrthOExaminationOrder.checkedVisionExaminationOs}
                                        />
                                    </div>
                                </div>
                                <div className="mb-3 row">
                                    <div className="col-8">
                                        <ReadOrEditInputField
                                            automation={`${props.field}_additie_os`}
                                            error={props.errors?.get(`customControlData.${props.field}_additie_os`)}
                                            fieldKey={`${props.field}_additie_os`}
                                            fieldValue={props.additie_os}
                                            isReadOnly={props.isReadOnly}
                                            label={''}
                                            placeholder={'add'}
                                            prefix="add"
                                            tabIndex={OrthOExaminationOrder.checkedVisionExaminationOs}
                                        />
                                    </div>
                                    <div className="col-8">
                                        <ReadOrEditInputField
                                            automation={`${props.field}_prisma_os`}
                                            error={props.errors?.get(`customControlData.${props.field}_prisma_os`)}
                                            fieldKey={`${props.field}_prisma_os`}
                                            fieldValue={props.prisma_os}
                                            isReadOnly={props.isReadOnly}
                                            label={''}
                                            onDoValidate={() => {
                                                if (props.onDoValidate) {
                                                    props.onDoValidate(`${props.field}_prisma_degrees_os`);
                                                    props.onDoValidate(`${props.field}_prisma_os`);
                                                }
                                            }}
                                            placeholder={'pr'}
                                            prefix="pr"
                                            tabIndex={OrthOExaminationOrder.checkedVisionExaminationOs}
                                        />
                                    </div>
                                    <div className="col-8">
                                        <ReadOrEditNumberInputField
                                            automation={`${props.field}_prisma_degrees_os`}
                                            error={props.errors?.get(
                                                `customControlData.${props.field}_prisma_degrees_os`
                                            )}
                                            fieldKey={`${props.field}_prisma_degrees_os`}
                                            fieldValue={props.prisma_degrees_os}
                                            isReadOnly={props.isReadOnly}
                                            label={''}
                                            onDoValidate={() => {
                                                if (props.onDoValidate) {
                                                    props.onDoValidate(`${props.field}_prisma_degrees_os`);
                                                    props.onDoValidate(`${props.field}_prisma_os`);
                                                }
                                            }}
                                            placeholder={'     °'}
                                            postfix="°"
                                            prefix=""
                                            tabIndex={OrthOExaminationOrder.checkedVisionExaminationOs}
                                        />
                                    </div>
                                </div>
                            </StyledBorderAndPadding>
                        </div>
                        <div className="col-sm-8"></div>
                    </div>
                </FormGroup>
            )}
        </>
    );

    function showRow() {
        if (!props.isReadOnly) {
            return true;
        } else if (
            props.additie_od ||
            props.additie_os ||
            props.as_od ||
            props.as_os ||
            props.cylindrisch_od ||
            props.cylindrisch_os ||
            props.prisma_od ||
            props.prisma_os ||
            props.sferisch_od ||
            props.sferisch_os ||
            props.prisma_degrees_od ||
            props.prisma_degrees_os
        ) {
            return true;
        }

        return false;
    }
}

function ReadOrEditInputField(props: IReadOrEditInputFieldProps) {
    if (!props.isReadOnly) {
        return <NullableTextField {...props} />;
    } else {
        return (
            <div style={{ whiteSpace: 'nowrap' }}>
                {props.fieldValue ? `${props.prefix} ${props.fieldValue}${props.postfix ?? ''}` : ''}
            </div>
        );
    }
}

function ReadOrEditNumberInputField(props: IReadOrEditInputFieldProps) {
    if (!props.isReadOnly) {
        return <NumberInput {...props} />;
    } else {
        return (
            <div style={{ whiteSpace: 'nowrap' }}>
                {props.fieldValue ? `${props.prefix} ${props.fieldValue}${props.postfix ?? ''}` : ''}
            </div>
        );
    }
}
